import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CategoryFacet, ProductFacets, ProductTypeKey } from "@/api";
import { ChevronDown16Icon, ChevronDown20Icon, ChevronRight16Icon, ChevronUp16Icon, ChevronUp20Icon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip } from "@/components/ui/chip";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/text-field";
import { colorDot } from "@osmix/ui-primitives";
import { type CategoryTreeRow, facetPath, visibleTreeRows } from "@/lib/category-tree";
import { activeFilterCount, clearedFilters, PRODUCT_TYPES, selectedProductTypes, withProductTypes } from "@/lib/product-query";
import { cn } from "@/lib/utils";
type Props = {
  facets: ProductFacets;
  params: URLSearchParams;
  setParams: (next: URLSearchParams) => void;
  // Current result count, rendered on the footer «Показать N товаров» button.
  // Passed from the catalog page; when absent the button reads just «Показать».
  total?: number;
  // Called by the footer «Показать» button — the host closes the sheet.
  onApply?: () => void;
  // Лента главной (A-11): категорий можно отметить несколько (`categoryId`
  // повторяется). В каталоге категория одна — это страница категории.
  multiCategory?: boolean;
};

/**
 * Панель «Все фильтры» по кадрам «Фильтры 1» 3844:150479 (как открывается),
 * «Фильтры дефолт» 3845:151131 и «Фильтры актив» 3845:151403.
 *
 * Состав и порядок макета: Товар → Категория → Размер → Цена → Цвет → SALE.
 * A-08 (Айталы 02.10): при открытии всё свёрнуто, раскрыт только «Товар» (у
 * него и стрелки нет); раздел, где уже что-то выбрано, открывается сам, чтобы
 * выбор был виден. Цвета — приглушённые, без количества товаров.
 *
 * Тело: отступы 10/20/10/30, зазор 16 между разделами; раздел = строка 40px
 * (600/16/20 #2E2E2E + шеврон 20) и через 5px плашка #F8F8F8 r12 с отступом 10.
 * Футер 10/20/30/30: «Сбросить фильтры» (серая, пока сбрасывать нечего — текст
 * #8A8A8B 46 %) и «Показать N товаров» (#2E2E2E r12 с тенью), обе 48px.
 *
 * Всё состояние — в `params` (адрес каталога или черновик ленты).
 */
export function CatalogFilters(t0) {
  const $ = _c(138);
  const {
    facets,
    params,
    setParams,
    total,
    onApply,
    multiCategory: t1
  } = t0;
  const multiCategory = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== params || $[1] !== setParams) {
    t2 = (key, value) => {
      const next = new URLSearchParams(params);
      const all = next.getAll(key);
      next.delete(key);
      const updated = all.includes(value) ? all.filter(v => v !== value) : [...all, value];
      for (const v_0 of updated) {
        next.append(key, v_0);
      }
      next.delete("page");
      setParams(next);
    };
    $[0] = params;
    $[1] = setParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const toggleMulti = t2;
  let t3;
  if ($[3] !== params || $[4] !== setParams) {
    t3 = (key_0, value_0) => {
      const next_0 = new URLSearchParams(params);
      if (value_0) {
        next_0.set(key_0, value_0);
      } else {
        next_0.delete(key_0);
      }
      next_0.delete("page");
      setParams(next_0);
    };
    $[3] = params;
    $[4] = setParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const setSingle = t3;
  let t4;
  if ($[6] !== params) {
    t4 = params.getAll("categoryId");
    $[6] = params;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selectedCategories = t4;
  let canReset;
  let sale;
  let showLabel;
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== facets || $[9] !== multiCategory || $[10] !== params || $[11] !== selectedCategories || $[12] !== setParams || $[13] !== setSingle || $[14] !== t || $[15] !== toggleMulti || $[16] !== total) {
    const selectedColors = params.getAll("color");
    let hasPrice;
    if ($[27] !== facets.categories || $[28] !== facets.sizeGroups || $[29] !== facets.sizes || $[30] !== multiCategory || $[31] !== params || $[32] !== selectedCategories || $[33] !== setParams || $[34] !== setSingle || $[35] !== t || $[36] !== toggleMulti || $[37] !== total) {
      const selectedSizes = params.getAll("size");
      let T0;
      let t12;
      let t13;
      let t14;
      let t15;
      let t16;
      if ($[47] !== params || $[48] !== setParams || $[49] !== t || $[50] !== total) {
        const types = selectedProductTypes(params);
        sale = params.get("sale") === "true";
        let t17;
        if ($[63] !== params) {
          t17 = params.get("priceMin") || params.get("priceMax");
          $[63] = params;
          $[64] = t17;
        } else {
          t17 = $[64];
        }
        hasPrice = Boolean(t17);
        canReset = activeFilterCount(params) > 0;
        const toggleType = type => {
          const on = types.includes(type);
          if (on && types.length === 1) {
            return;
          }
          setParams(withProductTypes(params, on ? types.filter(x => x !== type) : [...types, type]));
        };
        let t18;
        if ($[65] !== t || $[66] !== total) {
          t18 = typeof total === "number" ? t("filters.showCount", {
            count: total,
            defaultValue: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C {{count}} \u0442\u043E\u0432\u0430\u0440\u043E\u0432"
          }) : t("filters.show", {
            defaultValue: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C"
          });
          $[65] = t;
          $[66] = total;
          $[67] = t18;
        } else {
          t18 = $[67];
        }
        showLabel = t18;
        let t19;
        if ($[68] !== t) {
          t19 = t("catalogFilters.type.inStock", {
            defaultValue: "\u0412 \u043D\u0430\u043B\u0438\u0447\u0438\u0438"
          });
          $[68] = t;
          $[69] = t19;
        } else {
          t19 = $[69];
        }
        let t20;
        if ($[70] !== t) {
          t20 = t("catalogFilters.type.customizable", {
            defaultValue: "\u041A\u0430\u0441\u0442\u043E\u043C\u0438\u0437\u0430\u0446\u0438\u044F"
          });
          $[70] = t;
          $[71] = t20;
        } else {
          t20 = $[71];
        }
        let t21;
        if ($[72] !== t) {
          t21 = t("catalogFilters.type.madeToOrder", {
            defaultValue: "\u041D\u0430 \u0437\u0430\u043A\u0430\u0437"
          });
          $[72] = t;
          $[73] = t21;
        } else {
          t21 = $[73];
        }
        let t22;
        if ($[74] !== t19 || $[75] !== t20 || $[76] !== t21) {
          t22 = {
            "in-stock": t19,
            customizable: t20,
            "made-to-order": t21
          };
          $[74] = t19;
          $[75] = t20;
          $[76] = t21;
          $[77] = t22;
        } else {
          t22 = $[77];
        }
        const typeLabel = t22;
        t11 = "flex min-h-0 flex-1 flex-col font-sans";
        t5 = "flex flex-1 flex-col gap-4 overflow-y-auto pt-2.5 pr-5 pb-2.5 pl-[30px]";
        T0 = Section;
        if ($[78] !== t) {
          t15 = t("catalogFilters.product", {
            defaultValue: "\u0422\u043E\u0432\u0430\u0440"
          });
          $[78] = t;
          $[79] = t15;
        } else {
          t15 = $[79];
        }
        t16 = false;
        t12 = "flex flex-wrap gap-2.5";
        t13 = "filter-types";
        t14 = PRODUCT_TYPES.map(type_0 => <ValueChip key={type_0} active={types.includes(type_0)} onClick={() => toggleType(type_0)}>{typeLabel[type_0]}</ValueChip>);
        $[47] = params;
        $[48] = setParams;
        $[49] = t;
        $[50] = total;
        $[51] = T0;
        $[52] = canReset;
        $[53] = hasPrice;
        $[54] = sale;
        $[55] = showLabel;
        $[56] = t11;
        $[57] = t12;
        $[58] = t13;
        $[59] = t14;
        $[60] = t15;
        $[61] = t16;
        $[62] = t5;
      } else {
        T0 = $[51];
        canReset = $[52];
        hasPrice = $[53];
        sale = $[54];
        showLabel = $[55];
        t11 = $[56];
        t12 = $[57];
        t13 = $[58];
        t14 = $[59];
        t15 = $[60];
        t16 = $[61];
        t5 = $[62];
      }
      let t17;
      if ($[80] !== t12 || $[81] !== t13 || $[82] !== t14) {
        t17 = <div className={t12} data-testid={t13}>{t14}</div>;
        $[80] = t12;
        $[81] = t13;
        $[82] = t14;
        $[83] = t17;
      } else {
        t17 = $[83];
      }
      if ($[84] !== T0 || $[85] !== t15 || $[86] !== t16 || $[87] !== t17) {
        t6 = <T0 label={t15} collapsible={t16}>{t17}</T0>;
        $[84] = T0;
        $[85] = t15;
        $[86] = t16;
        $[87] = t17;
        $[88] = t6;
      } else {
        t6 = $[88];
      }
      if ($[89] !== facets.categories || $[90] !== multiCategory || $[91] !== selectedCategories || $[92] !== setSingle || $[93] !== t || $[94] !== toggleMulti) {
        t7 = facets.categories.length > 0 && <Section label={t("filters.category")} defaultOpen={selectedCategories.length > 0} bottomPad={true}><CategoryTree nodes={facets.categories} selected={selectedCategories} onToggle={value_1 => multiCategory ? toggleMulti("categoryId", value_1) : setSingle("categoryId", selectedCategories.includes(value_1) ? null : value_1)} expandLabel={t("filters.expand", {
            defaultValue: "\u0420\u0430\u0441\u043A\u0440\u044B\u0442\u044C"
          })} moreLabel={t("catalogFilters.showMore", {
            defaultValue: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0435\u0449\u0435"
          })} lessLabel={t("filters.showLess", {
            defaultValue: "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C"
          })} /></Section>;
        $[89] = facets.categories;
        $[90] = multiCategory;
        $[91] = selectedCategories;
        $[92] = setSingle;
        $[93] = t;
        $[94] = toggleMulti;
        $[95] = t7;
      } else {
        t7 = $[95];
      }
      t8 = facets.sizes.length > 0 && <Section label={t("filters.size")} defaultOpen={selectedSizes.length > 0}><div className="flex flex-col gap-3">{(facets.sizeGroups.length > 0 ? facets.sizeGroups : [{
            scale: "all",
            label: "",
            values: facets.sizes
          }]).map(group => <div key={group.scale} className="flex flex-col gap-1.5">{group.label && facets.sizeGroups.length > 1 && <span className="text-[12px] leading-4 text-ds-secondary">{group.label}</span>}<div className="flex flex-wrap gap-2.5">{group.values.map(s => <ValueChip key={s.value} active={selectedSizes.includes(s.value)} onClick={() => toggleMulti("size", s.value)}>{s.value}</ValueChip>)}</div></div>)}</div></Section>;
      $[27] = facets.categories;
      $[28] = facets.sizeGroups;
      $[29] = facets.sizes;
      $[30] = multiCategory;
      $[31] = params;
      $[32] = selectedCategories;
      $[33] = setParams;
      $[34] = setSingle;
      $[35] = t;
      $[36] = toggleMulti;
      $[37] = total;
      $[38] = canReset;
      $[39] = hasPrice;
      $[40] = sale;
      $[41] = showLabel;
      $[42] = t11;
      $[43] = t5;
      $[44] = t6;
      $[45] = t7;
      $[46] = t8;
    } else {
      canReset = $[38];
      hasPrice = $[39];
      sale = $[40];
      showLabel = $[41];
      t11 = $[42];
      t5 = $[43];
      t6 = $[44];
      t7 = $[45];
      t8 = $[46];
    }
    if ($[96] !== facets || $[97] !== hasPrice || $[98] !== params || $[99] !== setParams) {
      t9 = <PriceSection facets={facets} params={params} setParams={setParams} defaultOpen={hasPrice} />;
      $[96] = facets;
      $[97] = hasPrice;
      $[98] = params;
      $[99] = setParams;
      $[100] = t9;
    } else {
      t9 = $[100];
    }
    t10 = facets.colors.length > 0 && <Section label={t("filters.color")} defaultOpen={selectedColors.length > 0}><div className="grid grid-cols-2 gap-x-5">{facets.colors.map(c => {
          const on_0 = selectedColors.includes(c.value);
          return <button key={c.value} type="button" aria-pressed={on_0} onClick={() => toggleMulti("color", c.value)} className="flex min-w-0 items-center gap-2.5 rounded-[8px] py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"><span className={cn("size-6 shrink-0 rounded-full", on_0 && "shadow-[inset_0_0_0_2px_#FA5C2E]")} style={{
              background: mutedSwatch(c.value)
            }} /><span className="truncate text-[14px] font-medium leading-[18px] text-ds-ink-2">{c.value}</span></button>;
        })}</div></Section>;
    $[8] = facets;
    $[9] = multiCategory;
    $[10] = params;
    $[11] = selectedCategories;
    $[12] = setParams;
    $[13] = setSingle;
    $[14] = t;
    $[15] = toggleMulti;
    $[16] = total;
    $[17] = canReset;
    $[18] = sale;
    $[19] = showLabel;
    $[20] = t10;
    $[21] = t11;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    canReset = $[17];
    sale = $[18];
    showLabel = $[19];
    t10 = $[20];
    t11 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t12;
  if ($[101] !== setSingle) {
    t12 = on_1 => setSingle("sale", on_1 ? "true" : null);
    $[101] = setSingle;
    $[102] = t12;
  } else {
    t12 = $[102];
  }
  let t13;
  if ($[103] !== sale || $[104] !== t12) {
    t13 = <Switch checked={sale} onCheckedChange={t12} />;
    $[103] = sale;
    $[104] = t12;
    $[105] = t13;
  } else {
    t13 = $[105];
  }
  let t14;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="text-[16px] font-medium leading-[21px] text-ds-accent">SALE</span>;
    $[106] = t14;
  } else {
    t14 = $[106];
  }
  let t15;
  if ($[107] !== t13) {
    t15 = <label className="flex h-[41px] cursor-pointer items-center gap-2.5 self-start py-2.5">{t13}{t14}</label>;
    $[107] = t13;
    $[108] = t15;
  } else {
    t15 = $[108];
  }
  let t16;
  if ($[109] !== t10 || $[110] !== t15 || $[111] !== t5 || $[112] !== t6 || $[113] !== t7 || $[114] !== t8 || $[115] !== t9) {
    t16 = <div className={t5}>{t6}{t7}{t8}{t9}{t10}{t15}</div>;
    $[109] = t10;
    $[110] = t15;
    $[111] = t5;
    $[112] = t6;
    $[113] = t7;
    $[114] = t8;
    $[115] = t9;
    $[116] = t16;
  } else {
    t16 = $[116];
  }
  const t17 = !canReset;
  let t18;
  if ($[117] !== params || $[118] !== setParams) {
    t18 = () => setParams(clearedFilters(params));
    $[117] = params;
    $[118] = setParams;
    $[119] = t18;
  } else {
    t18 = $[119];
  }
  let t19;
  if ($[120] !== t) {
    t19 = t("catalogFilters.reset", {
      defaultValue: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440\u044B"
    });
    $[120] = t;
    $[121] = t19;
  } else {
    t19 = $[121];
  }
  let t20;
  if ($[122] !== t17 || $[123] !== t18 || $[124] !== t19) {
    t20 = <Button variant="gray" disabled={t17} onClick={t18} className="text-ds-ink-2">{t19}</Button>;
    $[122] = t17;
    $[123] = t18;
    $[124] = t19;
    $[125] = t20;
  } else {
    t20 = $[125];
  }
  let t21;
  if ($[126] !== onApply) {
    t21 = () => onApply?.();
    $[126] = onApply;
    $[127] = t21;
  } else {
    t21 = $[127];
  }
  let t22;
  if ($[128] !== showLabel || $[129] !== t21) {
    t22 = <Button onClick={t21} className="bg-ds-ink-2 shadow-[0_1px_4px_rgba(12,12,13,0.05),0_1px_4px_rgba(12,12,13,0.1)] hover:bg-ds-primary">{showLabel}</Button>;
    $[128] = showLabel;
    $[129] = t21;
    $[130] = t22;
  } else {
    t22 = $[130];
  }
  let t23;
  if ($[131] !== t20 || $[132] !== t22) {
    t23 = <div className="flex flex-col-reverse gap-2.5 pt-2.5 pr-5 pb-[30px] pl-[30px] sm:flex-row sm:items-center sm:justify-end sm:gap-4">{t20}{t22}</div>;
    $[131] = t20;
    $[132] = t22;
    $[133] = t23;
  } else {
    t23 = $[133];
  }
  let t24;
  if ($[134] !== t11 || $[135] !== t16 || $[136] !== t23) {
    t24 = <div className={t11}>{t16}{t23}</div>;
    $[134] = t11;
    $[135] = t16;
    $[136] = t23;
    $[137] = t24;
  } else {
    t24 = $[137];
  }
  return t24;
}

/**
 * Приглушённые тона кружков цвета из кадра 3845:151131 (Айталы 02.10: «цвета
 * приглушённые, как в макете»). Названия, которых нет в палитре макета,
 * получают тон общей палитры с убранной насыщенностью — в одной гамме.
 */
const MUTED_SWATCHES: Record<string, string> = {
  белый: "#F2F1ED",
  чёрный: "#2D2A26",
  черный: "#2D2A26",
  серый: "#80807F",
  бежевый: "#E5CDB5",
  коричневый: "#503629",
  синий: "#8B95A6",
  зелёный: "#95998A",
  зеленый: "#95998A",
  красный: "#B84727",
  розовый: "#DBBAB0",
  оранжевый: "#EC9F68",
  фиолетовый: "#B2B1C4",
  жёлтый: "#EBE4C0",
  желтый: "#EBE4C0"
};
function mutedSwatch(name: string): string {
  const key = name.trim().toLowerCase();
  const known = MUTED_SWATCHES[key];
  if (known) return known;
  const base = colorDot(name.trim().charAt(0).toUpperCase() + name.trim().slice(1).toLowerCase());
  return desaturate(base, 0.4);
}

// Убирает насыщенность (оставляет долю `keep`), светлоту не трогает.
function desaturate(hex: string, keep: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m?.[1]) return hex;
  const n = Number.parseInt(m[1], 16);
  const [r, g, b] = [n >> 16 & 255, n >> 8 & 255, n & 255].map(v => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return hex;
  const s = d / (1 - Math.abs(2 * l - 1)) * keep;
  let h = max === r ? (g - b) / d % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(h / 60 % 2 - 1));
  const mm = l - c / 2;
  const [r1, g1, b1] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const to = (v: number) => Math.round((v + mm) * 255).toString(16).padStart(2, "0");
  return `#${to(r1)}${to(g1)}${to(b1)}`;
}

// A labelled, collapsible section: label row 40px (600/16/20 #2E2E2E + chevron
// 20) and, 5px below, a #F8F8F8 r12 container with 10px padding (`bottomPad` —
// 20px снизу, как у списков с «Показать еще»). Свёрнут по умолчанию.
function Section(t0) {
  const $ = _c(22);
  const {
    label,
    children,
    bottomPad: t1,
    collapsible: t2,
    defaultOpen: t3
  } = t0;
  const bottomPad = t1 === undefined ? false : t1;
  const collapsible = t2 === undefined ? true : t2;
  const defaultOpen = t3 === undefined ? false : t3;
  const [open, setOpen] = useState(defaultOpen || !collapsible);
  const t4 = bottomPad && "pb-5";
  let t5;
  if ($[0] !== t4) {
    t5 = cn("rounded-[12px] bg-ds-quaternary p-2.5", t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== children || $[3] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[2] = children;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const body = t6;
  if (!collapsible) {
    let t7;
    if ($[5] !== label) {
      t7 = <div className="flex h-10 items-center"><span className="text-[16px] font-semibold leading-5 text-ds-ink-2">{label}</span></div>;
      $[5] = label;
      $[6] = t7;
    } else {
      t7 = $[6];
    }
    let t8;
    if ($[7] !== body || $[8] !== t7) {
      t8 = <div className="flex flex-col gap-[5px]">{t7}{body}</div>;
      $[7] = body;
      $[8] = t7;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setOpen(_temp);
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== label) {
    t8 = <span className="text-[16px] font-semibold leading-5 text-ds-ink-2">{label}</span>;
    $[11] = label;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== open) {
    t9 = open ? <ChevronUp20Icon className="text-ds-ink-2" /> : <ChevronDown20Icon className="text-ds-ink-2" />;
    $[13] = open;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== open || $[16] !== t8 || $[17] !== t9) {
    t10 = <button type="button" onClick={t7} aria-expanded={open} className="flex h-10 items-center justify-between rounded-[12px] text-left outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t8}{t9}</button>;
    $[15] = open;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = open && body;
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <div className="flex flex-col gap-[5px]">{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}

// Чип значения фильтра — DS `Chip` размера lg (.chips 38px: pad 10/14,
// 500/14/18; обычный #1F1F1F 3 % + 4 %, выбранный #FA5C2E 5 % + обводка).
function _temp(v) {
  return !v;
}
function ValueChip(t0) {
  const $ = _c(4);
  const {
    active,
    onClick,
    children
  } = t0;
  let t1;
  if ($[0] !== active || $[1] !== children || $[2] !== onClick) {
    t1 = <Chip size="lg" selected={active} onClick={onClick}>{children}</Chip>;
    $[0] = active;
    $[1] = children;
    $[2] = onClick;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// Целое число с разрядами через тонкий пробел (как цены витрины): «300 500».
const groupDigits = (value: string): string => value.replace(/\B(?=(\d{3})+(?!\d))/g, "\u202F");
// «500 ₽» как в поле макета: «₽» неразрывно после числа (как у formatPrice).
const withRuble = (value: string): string => value ? `${groupDigits(value)}\u00A0₽` : "";
const digitsOnly = (value: string): string => value.replace(/\D/g, "");

// Цена — a two-handle range slider synced to «от»/«до» fields. Быстрые
// пресеты («до 5 000 ₽» и т. п.) убраны по правке заказчика 12.09.2026: диапазоны
// читались как непонятные. Всё пишется в URL (priceMin / priceMax), с
// ограничением по фактическому разбросу цен фасета.
//
// Поле по макету: 40px r20, заливка #1F1F1F 3 %, текст 500/14/18 #2E2E2E с «₽»
// («500 ₽», «300 500 ₽»), подписи «от»/«до» слева 400/16 #000 50 %. Без стрелок
// числового поля. В фокусе — тонкая тёмная обводка, как у полей ввода макета.
function PriceSection(t0) {
  const $ = _c(71);
  const {
    facets,
    params,
    setParams,
    defaultOpen
  } = t0;
  const {
    t
  } = useTranslation();
  const min = Math.floor(facets.price.min);
  const max = Math.ceil(facets.price.max);
  let t1;
  if ($[0] !== min || $[1] !== params) {
    t1 = Number(params.get("priceMin")) || min;
    $[0] = min;
    $[1] = params;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== max || $[4] !== params) {
    t2 = Number(params.get("priceMax")) || max;
    $[3] = max;
    $[4] = params;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = [t1, t2];
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const [range, setRange] = useState(t3);
  let t4;
  if ($[9] !== params) {
    t4 = params.get("priceMin") ?? "";
    $[9] = params;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [lo, setLo] = useState(t4);
  let t5;
  if ($[11] !== params) {
    t5 = params.get("priceMax") ?? "";
    $[11] = params;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [hi, setHi] = useState(t5);
  const [focused, setFocused] = useState(null);
  let t6;
  let t7;
  if ($[13] !== max || $[14] !== min || $[15] !== params) {
    t6 = () => {
      const urlMin = params.get("priceMin");
      const urlMax = params.get("priceMax");
      setRange([Number(urlMin) || min, Number(urlMax) || max]);
      setLo(urlMin ?? "");
      setHi(urlMax ?? "");
    };
    t7 = [params, min, max];
    $[13] = max;
    $[14] = min;
    $[15] = params;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  if (min >= max) {
    return null;
  }
  let t8;
  if ($[18] !== max || $[19] !== min || $[20] !== params || $[21] !== setParams) {
    t8 = (loVal, hiVal) => {
      const next = new URLSearchParams(params);
      if (loVal != null && loVal > min) {
        next.set("priceMin", String(loVal));
      } else {
        next.delete("priceMin");
      }
      if (hiVal != null && hiVal < max) {
        next.set("priceMax", String(hiVal));
      } else {
        next.delete("priceMax");
      }
      next.delete("page");
      setParams(next);
    };
    $[18] = max;
    $[19] = min;
    $[20] = params;
    $[21] = setParams;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const commit = t8;
  let t9;
  if ($[23] !== commit || $[24] !== hi || $[25] !== lo || $[26] !== max || $[27] !== min) {
    t9 = () => {
      let loVal_0 = lo.trim() === "" ? undefined : Number(lo);
      let hiVal_0 = hi.trim() === "" ? undefined : Number(hi);
      if (loVal_0 != null && hiVal_0 != null && loVal_0 > hiVal_0) {
        [loVal_0, hiVal_0] = [hiVal_0, loVal_0];
      }
      setRange([loVal_0 ?? min, hiVal_0 ?? max]);
      commit(loVal_0, hiVal_0);
    };
    $[23] = commit;
    $[24] = hi;
    $[25] = lo;
    $[26] = max;
    $[27] = min;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const commitInputs = t9;
  let t10;
  if ($[29] !== commitInputs || $[30] !== focused) {
    t10 = (key, value, set, placeholder, label) => <label className="flex min-w-0 flex-1 items-center gap-2"><span className="text-[16px] leading-[22px] text-ds-secondary">{label}</span><Input type="text" inputMode="numeric" value={focused === key ? groupDigits(value) : withRuble(value)} placeholder={withRuble(String(placeholder))} aria-label={label} onFocus={() => setFocused(key)} onChange={e => set(digitsOnly(e.target.value))} onBlur={() => {
        setFocused(null);
        commitInputs();
      }} onKeyDown={e_0 => e_0.key === "Enter" && commitInputs()} className="w-full font-sans text-ds-ink-2 placeholder:text-ds-ink-2" /></label>;
    $[29] = commitInputs;
    $[30] = focused;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const field = t10;
  let t11;
  if ($[32] !== t) {
    t11 = t("filters.price");
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== max || $[35] !== min) {
    t12 = Math.round((max - min) / 100);
    $[34] = max;
    $[35] = min;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const t13 = Math.max(1, t12);
  let t14;
  if ($[37] !== max || $[38] !== min) {
    t14 = v => {
      const lv = v[0] ?? min;
      const hv = v[1] ?? max;
      setRange([lv, hv]);
      setLo(lv > min ? String(lv) : "");
      setHi(hv < max ? String(hv) : "");
    };
    $[37] = max;
    $[38] = min;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== commit || $[41] !== max || $[42] !== min) {
    t15 = v_0 => commit(v_0[0] ?? min, v_0[1] ?? max);
    $[40] = commit;
    $[41] = max;
    $[42] = min;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== max || $[45] !== min || $[46] !== range || $[47] !== t13 || $[48] !== t14 || $[49] !== t15) {
    t16 = <Slider min={min} max={max} step={t13} value={range} onValueChange={t14} onValueCommit={t15} className="py-[5.5px] [&_[data-slot=slider-range]]:bg-ds-ink-2 [&_[data-slot=slider-thumb]]:size-[21px] [&_[data-slot=slider-thumb]]:border-ds-ink-2 [&_[data-slot=slider-thumb]]:bg-ds-ink-2 [&_[data-slot=slider-track]]:h-1! [&_[data-slot=slider-track]]:bg-ds-tertiary" />;
    $[44] = max;
    $[45] = min;
    $[46] = range;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== field || $[52] !== lo || $[53] !== min || $[54] !== t) {
    t17 = field("lo", lo, setLo, min, t("filters.priceFrom"));
    $[51] = field;
    $[52] = lo;
    $[53] = min;
    $[54] = t;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== field || $[57] !== hi || $[58] !== max || $[59] !== t) {
    t18 = field("hi", hi, setHi, max, t("filters.priceTo"));
    $[56] = field;
    $[57] = hi;
    $[58] = max;
    $[59] = t;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== t17 || $[62] !== t18) {
    t19 = <div className="flex items-center gap-4">{t17}{t18}</div>;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== t16 || $[65] !== t19) {
    t20 = <div className="flex flex-col gap-3">{t16}{t19}</div>;
    $[64] = t16;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== defaultOpen || $[68] !== t11 || $[69] !== t20) {
    t21 = <Section label={t11} defaultOpen={defaultOpen}>{t20}</Section>;
    $[67] = defaultOpen;
    $[68] = t11;
    $[69] = t20;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  return t21;
}

// Категории — дерево до трёх уровней (Женщинам → Обувь → Кроссовки и кеды):
// видны корни, ветка раскрывается стрелкой, путь к выбранной категории раскрыт
// всегда. Выбрать можно узел любого уровня — шлюз берёт всю его ветку. По
// макету строка — чекбокс 20 r5 + 500/16/21 #1F1F1F, без счётчиков; в списке
// первые 4 строки и «Показать еще» (600/14 + шеврон 16).
const CATEGORY_ROWS = 4;
function CategoryTree(t0) {
  const $ = _c(27);
  const {
    nodes,
    selected,
    onToggle,
    expandLabel,
    moreLabel,
    lessLabel
  } = t0;
  const [expanded, setExpanded] = useState(_temp2);
  const [showAll, setShowAll] = useState(false);
  let collapsible;
  let t1;
  let t2;
  let t3;
  if ($[0] !== expandLabel || $[1] !== expanded || $[2] !== nodes || $[3] !== onToggle || $[4] !== selected || $[5] !== showAll) {
    let t4;
    if ($[10] !== nodes) {
      t4 = id => facetPath(nodes, id).map(_temp3).filter(v => v !== id);
      $[10] = nodes;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    const forced = new Set(selected.flatMap(t4));
    const rows = visibleTreeRows(nodes, new Set([...expanded, ...forced]), null);
    collapsible = rows.length > CATEGORY_ROWS;
    const visible = showAll || !collapsible ? rows : rows.slice(0, CATEGORY_ROWS);
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = (value, open) => setExpanded(prev => {
        const next = new Set(prev);
        if (open) {
          next.delete(value);
        } else {
          next.add(value);
        }
        return next;
      });
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    const toggleOpen = t5;
    t1 = "flex flex-col";
    t2 = "category-tree";
    let t6;
    if ($[13] !== expandLabel || $[14] !== onToggle || $[15] !== selected) {
      t6 = t7 => {
        const {
          node,
          depth,
          hasChildren,
          open: open_0
        } = t7;
        const checked = selected.includes(node.value);
        return <div key={node.value} className="flex items-center" style={depth ? {
          paddingLeft: depth * 20
        } : undefined}><label data-category={node.value} className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 py-2.5 has-[:disabled]:cursor-default"><Checkbox checked={checked} disabled={node.count === 0} onCheckedChange={() => onToggle(node.value)} className="bg-transparent" /><span className="truncate text-[16px] font-medium leading-[21px] text-ds-primary">{node.label}</span></label>{hasChildren && <button type="button" aria-label={`${expandLabel}: ${node.label}`} aria-expanded={open_0} data-expand={node.value} onClick={() => toggleOpen(node.value, open_0)} className="ml-1 flex size-8 shrink-0 items-center justify-center rounded-full text-ds-muted outline-none hover:text-ds-primary focus-visible:ring-2 focus-visible:ring-ds-focus"><ChevronRight16Icon className={cn("transition-transform", open_0 && "rotate-90")} /></button>}</div>;
      };
      $[13] = expandLabel;
      $[14] = onToggle;
      $[15] = selected;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t3 = visible.map(t6);
    $[0] = expandLabel;
    $[1] = expanded;
    $[2] = nodes;
    $[3] = onToggle;
    $[4] = selected;
    $[5] = showAll;
    $[6] = collapsible;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    collapsible = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  let t4;
  if ($[17] !== collapsible || $[18] !== lessLabel || $[19] !== moreLabel || $[20] !== showAll) {
    t4 = collapsible && <button type="button" onClick={() => setShowAll(_temp4)} className="mt-2.5 flex items-center gap-1.5 self-start rounded-[10px] text-[14px] font-semibold leading-[18px] text-ds-ink-2 outline-none transition-colors hover:text-ds-accent focus-visible:ring-2 focus-visible:ring-ds-focus">{showAll ? lessLabel : moreLabel}{showAll ? <ChevronUp16Icon /> : <ChevronDown16Icon />}</button>;
    $[17] = collapsible;
    $[18] = lessLabel;
    $[19] = moreLabel;
    $[20] = showAll;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== t1 || $[23] !== t2 || $[24] !== t3 || $[25] !== t4) {
    t5 = <div className={t1} data-testid={t2}>{t3}{t4}</div>;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
}
function _temp4(v_0) {
  return !v_0;
}
function _temp3(n) {
  return n.value;
}
function _temp2() {
  return new Set();
}